import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getChannels, createChannel, updateChannel, deleteChannel } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash, VideoCamera, CalendarBlank } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const COLORS = [
  "#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#6366F1",
  "#EC4899", "#8B5CF6", "#14B8A6", "#F97316", "#64748B",
];

const SCHEDULES = ["1x/week", "2x/week", "3x/week", "Daily", "Bi-weekly", "Monthly"];

export default function Channels() {
  const { hasPermission } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [deletingChannel, setDeletingChannel] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    niche: "",
    color: "#3B82F6",
    posting_schedule: "1x/week",
    description: "",
    status: "active",
  });

  const fetchChannels = async () => {
    try {
      const response = await getChannels();
      setChannels(response.data);
    } catch (err) {
      toast.error("Failed to load channels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenCreate = () => {
    setEditingChannel(null);
    setFormData({
      name: "",
      niche: "",
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      posting_schedule: "1x/week",
      description: "",
      status: "active",
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (channel) => {
    setEditingChannel(channel);
    setFormData({
      name: channel.name,
      niche: channel.niche,
      color: channel.color,
      posting_schedule: channel.posting_schedule,
      description: channel.description || "",
      status: channel.status,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingChannel) {
        await updateChannel(editingChannel.id, formData);
        toast.success("Channel updated");
      } else {
        await createChannel(formData);
        toast.success("Channel created");
      }
      setDialogOpen(false);
      fetchChannels();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save channel");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteChannel(deletingChannel.id);
      toast.success("Channel deleted");
      setDeleteDialogOpen(false);
      setDeletingChannel(null);
      fetchChannels();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete channel");
    }
  };

  const canCreate = hasPermission("create_channels");
  const canEdit = hasPermission("edit_channels");
  const canDelete = hasPermission("delete_channels");

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="surface-card p-6">
            <div className="skeleton h-6 w-3/4 mb-4" />
            <div className="skeleton h-4 w-1/2 mb-2" />
            <div className="skeleton h-4 w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">Channels</h1>
          <p className="text-gray-400 text-sm">{channels.length} channels</p>
        </div>
        {canCreate && (
          <Button onClick={handleOpenCreate} className="btn-primary" data-testid="create-channel-btn">
            <Plus size={18} className="mr-2" />
            New Channel
          </Button>
        )}
      </div>

      {/* Channel Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {channels.map((channel) => (
          <div
            key={channel.id}
            className="surface-card p-6 hover:border-white/10 transition-all group"
            data-testid={`channel-card-${channel.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <Link to={`/channels/${channel.id}`} className="flex items-center gap-3 flex-1">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${channel.color}20` }}
                >
                  <VideoCamera size={20} style={{ color: channel.color }} />
                </div>
                <div>
                  <h3 className="text-white font-medium group-hover:text-blue-400 transition-colors">
                    {channel.name}
                  </h3>
                  <p className="text-sm text-gray-400">{channel.niche}</p>
                </div>
              </Link>
              {(canEdit || canDelete) && (
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canEdit && (
                    <button
                      onClick={() => handleOpenEdit(channel)}
                      className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white"
                      data-testid={`edit-channel-${channel.id}`}
                    >
                      <Pencil size={16} />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => {
                        setDeletingChannel(channel);
                        setDeleteDialogOpen(true);
                      }}
                      className="p-2 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400"
                      data-testid={`delete-channel-${channel.id}`}
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Videos</p>
                <p className="text-white font-medium">{channel.video_count || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Schedule</p>
                <p className="text-white font-medium">{channel.posting_schedule}</p>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
              <span
                className={`badge ${
                  channel.status === "active"
                    ? "bg-green-500/20 text-green-400 border-green-500/30"
                    : channel.status === "paused"
                    ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                    : "bg-gray-500/20 text-gray-400 border-gray-500/30"
                }`}
              >
                {channel.status}
              </span>
              <Link
                to={`/channels/${channel.id}`}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                View details →
              </Link>
            </div>
          </div>
        ))}
      </div>

      {channels.length === 0 && (
        <div className="text-center py-16">
          <VideoCamera size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No channels yet</h3>
          <p className="text-gray-400 mb-4">Create your first channel to get started</p>
          {canCreate && (
            <Button onClick={handleOpenCreate} className="btn-primary">
              <Plus size={18} className="mr-2" />
              Create Channel
            </Button>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingChannel ? "Edit Channel" : "Create Channel"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Channel Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="My YouTube Channel"
                required
                data-testid="channel-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Niche</Label>
              <Input
                value={formData.niche}
                onChange={(e) => setFormData({ ...formData, niche: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Education, Comedy, etc."
                required
                data-testid="channel-niche-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-lg transition-all ${
                        formData.color === color
                          ? "ring-2 ring-white ring-offset-2 ring-offset-[#1A1A2E]"
                          : ""
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`channel-color-${color}`}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Schedule</Label>
                <Select
                  value={formData.posting_schedule}
                  onValueChange={(value) =>
                    setFormData({ ...formData, posting_schedule: value })
                  }
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="channel-schedule-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">
                    {SCHEDULES.map((schedule) => (
                      <SelectItem key={schedule} value={schedule} className="text-white">
                        {schedule}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="channel-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">
                  <SelectItem value="active" className="text-white">Active</SelectItem>
                  <SelectItem value="paused" className="text-white">Paused</SelectItem>
                  <SelectItem value="concept" className="text-white">Concept</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white min-h-[80px]"
                placeholder="What's this channel about?"
                data-testid="channel-description-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 btn-secondary"
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="channel-submit-btn">
                {editingChannel ? "Save Changes" : "Create Channel"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Channel?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete "{deletingChannel?.name}" and all its videos.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="confirm-delete-channel-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
